import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  FaUserSecret,
  FaBuilding,
  FaGlobe,
  FaSearch,
  FaFileAlt,
  FaMobileAlt,
  FaArrowRight,
  FaShieldAlt,
  FaClock,
  FaCheckCircle,
  FaStar,
  FaGem,
  FaRocket,
} from "react-icons/fa";
import "../styles/ServicesSection.css";

const ServicesSection = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const services = [
    {
      id: 1,
      title: "Pre-Marriage Verification",
      description: "Complete background check of prospective spouses including family, career, and character verification.",
      icon: <FaUserSecret />,
      link: "/services",
      color: "#667eea",
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      features: ["Background Check", "Family Verification", "Career Check"],
      popular: true,
    },
    {
      id: 2,
      title: "Cheating Spouse Investigation",
      description: "Discreet surveillance and evidence collection for suspected infidelity cases.",
      icon: <FaSearch />,
      link: "/services",
      color: "#f093fb",
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      features: ["Surveillance", "Evidence Collection", "Digital Forensics"],
      popular: true,
    },
    {
      id: 3,
      title: "Corporate Due Diligence",
      description: "Comprehensive background verification of business partners, vendors, and employees.",
      icon: <FaBuilding />,
      link: "/services",
      color: "#4facfe",
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      features: ["Vendor Verification", "Background Check", "Risk Assessment"],
      popular: false,
    },
    {
      id: 4,
      title: "Digital Evidence Collection",
      description: "Expert digital forensics and cyber investigation services.",
      icon: <FaMobileAlt />,
      link: "/services",
      color: "#43e97b",
      gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
      features: ["Cyber Forensics", "Data Recovery", "Digital Evidence"],
      popular: false,
    },
    {
      id: 5,
      title: "NRI Matrimonial Investigation",
      description: "Specialized investigation services for NRIs facing matrimonial issues.",
      icon: <FaGlobe />,
      link: "/services",
      color: "#fa709a",
      gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
      features: ["NRI Services", "Matrimonial Check", "Global Support"],
      popular: true,
    },
    {
      id: 6,
      title: "Missing Person Tracing",
      description: "Professional tracing services for missing persons and debt recovery.",
      icon: <FaFileAlt />,
      link: "/services",
      color: "#a18cd1",
      gradient: "linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)",
      features: ["Tracing Services", "Debt Recovery", "Location Detection"],
      popular: false,
    },
  ];

  return (
    <section className="services-section-premium" ref={sectionRef}>
      {/* Animated Background */}
      <div className="services-premium-bg">
        <div className="bg-gradient-1"></div>
        <div className="bg-gradient-2"></div>
        <div className="bg-gradient-3"></div>
        <div className="bg-particles">
          {[...Array(20)].map((_, i) => (
            <div key={i} className="particle" style={{ animationDelay: `${i * 0.5}s` }}></div>
          ))}
        </div>
      </div>

      <div className="services-premium-container">
        {/* Header Section */}
        <div className="services-premium-header">
          <div className="header-floating-icon">
            <FaGem className="floating-gem" />
          </div>
          <div className="services-premium-badge">
            <FaRocket className="badge-rocket" />
            <span>Premium Investigation Services</span>
          </div>
          <h2 className="services-premium-title">
            World-Class  <span className="title-gradient-premium"> Investigation Solutions</span>
           
          </h2>
          <div className="title-premium-decoration">
            <div className="decoration-line"></div>
            <div className="decoration-diamond"></div>
            <div className="decoration-line"></div>
          </div>
          <p className="services-premium-subtitle">
            We deliver excellence through cutting-edge technology, experienced professionals,
            and unwavering commitment to truth. Choose from our comprehensive range of
            investigative services tailored to your needs.
          </p>
        </div>

        {/* Services Grid */}
        <div className="services-premium-grid">
          {services.map((service, index) => (
            <div
              key={service.id}
              className={`service-premium-card ${hoveredCard === service.id ? "hovered" : ""} ${isVisible ? "visible" : ""}`}
              onMouseEnter={() => setHoveredCard(service.id)}
              onMouseLeave={() => setHoveredCard(null)}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="card-premium-bg" style={{ background: service.gradient }}></div>
              
              {service.popular && (
                <div className="premium-popular-badge">
                  <FaStar className="popular-star" />
                  <span>Most Popular</span>
                </div>
              )}
              
              <div className="card-premium-icon-wrapper">
                <div className="card-premium-icon" style={{ background: `${service.color}15`, color: service.color }}>
                  {service.icon}
                </div>
              </div>
              
              <h3 className="card-premium-title">{service.title}</h3>
              <p className="card-premium-description">{service.description}</p>
              
              <div className="card-premium-features">
                {service.features.map((feature, idx) => (
                  <div key={idx} className="premium-feature">
                    <FaCheckCircle className="feature-check-icon" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
              
              <Link to={service.link} className="card-premium-link">
                <span>Discover Service</span>
                <FaArrowRight className="link-arrow-icon" />
              </Link>
              
              <div className="card-premium-shine"></div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="services-premium-footer">
          <div className="footer-stats-grid">
            <div className="footer-stat">
              <div className="stat-icon-circle">
                <FaCheckCircle />
              </div>
              <div>
                <span className="footer-stat-number">5000+</span>
                <span className="footer-stat-label">Cases Solved</span>
              </div>
            </div>
            <div className="footer-stat">
              <div className="stat-icon-circle">
                <FaStar />
              </div>
              <div>
                <span className="footer-stat-number">98%</span>
                <span className="footer-stat-label">Success Rate</span>
              </div>
            </div>
            <div className="footer-stat">
              <div className="stat-icon-circle">
                <FaShieldAlt />
              </div>
              <div>
                <span className="footer-stat-number">24/7</span>
                <span className="footer-stat-label">Emergency Support</span>
              </div>
            </div>
            <div className="footer-stat">
              <div className="stat-icon-circle">
                <FaClock />
              </div>
              <div>
                <span className="footer-stat-number">15+</span>
                <span className="footer-stat-label">Years Experience</span>
              </div>
            </div>
          </div>
          
          <Link to="/services" className="premium-cta-btn">
            <span>Explore All Services</span>
            <FaArrowRight />
            <div className="btn-glow"></div>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;